import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { theme, sectionCardStyle, sectionHeaderRow, sectionTitleStyle, sectionIconWrap, sectionIconPaths } from '../../theme';

const SectionIcon = ({ name }) => (
  <span style={sectionIconWrap}>
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
      stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: sectionIconPaths[name] || '' }} />
  </span>
);

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

export default function DocumentsDossier({ dossierId, procedureId, dossier, onStatusChange }) {
  const { t } = useLanguage();
  const { isVoyageur, user, isReadOnly } = useAuth();
  // Phase D5 : qui peut marquer l'appel de fonds payé (Accounts, Admin, Manager, IT)
  const canMarkAfPaid = ['accounts','souscripteur','manager','it'].includes(user?.role);
  const canUnmarkAfPaid = ['souscripteur','manager','it'].includes(user?.role);

  const [docs, setDocs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [addingDoc, setAddingDoc]   = useState(false);
  const [newDocNom, setNewDocNom]   = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError]           = useState('');
  const [showConfirmAF, setShowConfirmAF] = useState(false);
  const [payingId, setPayingId]     = useState(null);
  const fileInputGeneral = useRef(null);
  const fileInputDoc     = useRef(null);
  const activeDocId      = useRef(null);

  // Phase D5 : souhait normalisé du dossier (maintien_voyage → maintien)
  const souhaitDossierNorm = (() => {
    const s = dossier?.souhait_client || dossier?.souhait_voyageur || '';
    return s === 'maintien_voyage' ? 'maintien' : s;
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await authFetch(`${BASE}/dossiers/${dossierId}/admin-docs`);
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { if (dossierId) load(); }, [load]);

  // Générer le PDF, le sauvegarder, et l'afficher dans la liste
  async function genererPdf() {
    setPdfLoading(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/dossiers/${dossierId}/admin-docs/save-pdf`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ procedureId }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => [data, ...prev]);
      // La génération ne change PLUS le statut : c'est la confirmation d'envoi
      // qui pilote le workflow. On notifie quand même le parent pour rafraîchir la liste.
      if (onStatusChange) onStatusChange();
      // Télécharger aussi directement
      await telecharger(data);
    } catch(e) { setError(e.message); }
    finally { setPdfLoading(false); }
  }

  // Confirmer l'ENVOI de l'appel de fonds → bascule en "Attente paiement du solde",
  // rend la section solde permanente et verrouille la génération.
  const [confirmingAF, setConfirmingAF] = useState(false);
  async function confirmerAppelFonds() {
    setConfirmingAF(true); setError('');
    try {
      const res = await authFetch(`${BASE}/dossiers/${dossierId}/confirmer-appel-fonds`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setShowConfirmAF(false);
      if (onStatusChange) onStatusChange();
    } catch(e) { setError(e.message); }
    finally { setConfirmingAF(false); }
  }

  // Phase D5 : marquer / annuler l'appel de fonds comme payé
  async function marquerPaye(doc) {
    setPayingId(doc.id); setError('');
    try {
      const res = await authFetch(`${BASE}/docs-dossier/${doc.id}/marquer-paye`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===doc.id ? data : d));
    } catch(e) { setError(e.message); }
    finally { setPayingId(null); }
  }
  async function annulerPaye(doc) {
    setPayingId(doc.id); setError('');
    try {
      const res = await authFetch(`${BASE}/docs-dossier/${doc.id}/annuler-paye`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===doc.id ? data : d));
    } catch(e) { setError(e.message); }
    finally { setPayingId(null); }
  }

  async function telecharger(doc) {
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${doc.id}/fichier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = doc.fichier_nom || doc.nom;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { setError(e.message); }
  }

  async function ouvrirPreview(doc) {
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${doc.id}/fichier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewDoc(doc);
    } catch(e) { setError(e.message); }
  }

  function fermerPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewDoc(null);
  }

  async function supprimerDoc(docId) {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await authFetch(`${BASE}/docs-dossier/${docId}`, { method:'DELETE' });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch(e) { setError(e.message); }
  }

  async function addCustomDoc() {
    if (!newDocNom.trim()) return;
    try {
      const res  = await authFetch(`${BASE}/dossiers/${dossierId}/admin-docs`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ nom: newDocNom.trim() }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => [...prev, data]);
      setNewDocNom(''); setAddingDoc(false);
    } catch(e) { setError(e.message); }
  }

  function triggerUpload(docId) {
    activeDocId.current = docId;
    fileInputDoc.current.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !activeDocId.current) return;
    const docId = activeDocId.current;
    setUploadingId(docId);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${docId}/upload`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
    } catch(e) { setError(e.message); }
    finally { setUploadingId(null); activeDocId.current = null; e.target.value = ''; }
  }

  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '';

  return (
    <div style={sectionCardStyle}>
      <input type="file" ref={fileInputDoc} style={{display:'none'}}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={handleFileChange}/>

      {/* Header */}
      <div style={sectionHeaderRow}>
        <div style={sectionTitleStyle}><SectionIcon name="documents" />{t('sinistres.docs_admin_title')}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          {/* Section verrouillée : appel de fonds confirmé → plus de génération possible */}
          {!isVoyageur && souhaitDossierNorm === 'maintien' && dossier?.appel_fonds_confirme ? (
            <span style={{ fontSize:11, color:'#5B21B6', background:'#F5F3FF', border:'1px solid #C4B5FD',
              borderRadius:99, padding:'5px 12px', fontWeight:500, display:'inline-flex', alignItems:'center', gap:5 }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#5B21B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                dangerouslySetInnerHTML={{ __html: sectionIconPaths.lock }} />
              {t('phase_solde.af_confirmed_badge')}
            </span>
          ) : (
          /* "Générer l'appel de fonds" : maintien + dossier administratif validé */
          !isVoyageur && !isReadOnly && souhaitDossierNorm === 'maintien' && (
            dossier?.dossier_admin_valide ? (
              <>
                <button onClick={genererPdf} disabled={pdfLoading}
                  style={{ padding:'8px 16px', borderRadius:theme.radius, border:`0.5px solid ${theme.borderStrong}`,
                    background:theme.surface, color:theme.textSecondary, cursor:'pointer', fontSize:13,
                    fontWeight:500, opacity:pdfLoading?0.6:1,
                    display:'inline-flex', alignItems:'center', gap:6 }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: sectionIconPaths.appel }} />
                  {pdfLoading ? t('sinistres.appel_fonds_dl') : t('sinistres.docs_admin_generer_pdf')}
                </button>
                {/* Confirmer l'envoi : visible dès qu'un appel de fonds a été généré */}
                {docs.some(d => d.categorie==='appel_fonds' || d.type==='appel_fonds') && (
                  <button onClick={()=>setShowConfirmAF(true)}
                    style={{ padding:'8px 16px', borderRadius:theme.radius, border:'none',
                      background:theme.orange, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500,
                      display:'inline-flex', alignItems:'center', gap:6 }}>
                    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5l9 -9"/></svg>
                    {t('phase_solde.af_confirm_btn')}
                  </button>
                )}
              </>
            ) : (
              <span style={{ fontSize:11, color:'#92400E', background:'#FEF3C7', border:'1px solid #FCD34D',
                borderRadius:7, padding:'7px 12px', fontWeight:600, maxWidth:280 }}>
                ⚠ {t('phase_solde.gen_blocked')}
              </span>
            )
          )
          )}
        </div>
      </div>

      {error && (
        <div style={{ background:'#FEE2E2', border:'1px solid #FCA5A5', borderRadius:7,
          padding:'8px 12px', marginBottom:12, fontSize:13, color:'#991B1B' }}>⚠ {error}</div>
      )}

      {/* Liste des documents */}
      {loading && <div style={{ color:'#94A3B8', fontSize:13, textAlign:'center', padding:12 }}>
        {t('sinistres.loading')}
      </div>}

      {!loading && docs.length === 0 && (
        <div style={{ color:'#94A3B8', fontSize:13, textAlign:'center', padding:'16px 0' }}>
          {t('sinistres.docs_admin_aucun')}
        </div>
      )}

      {docs.length > 0 && (
        <div style={{ border:'0.5px solid #E6E9ED', borderRadius:8, overflow:'hidden', marginBottom:12 }}>
          {docs.map((doc, i) => (
            <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:12,
              padding:'11px 14px', borderBottom: i<docs.length-1?'1px solid #F1F5F9':'none',
              background: i%2===0?'#fff':'#FAFAFA' }}>
              {/* Icône type */}
              <span style={{ fontSize:18, flexShrink:0 }}>
                {doc.fichier_mime==='application/pdf' ? '📄' :
                 doc.fichier_mime?.startsWith('image/') ? '🖼' : '📎'}
              </span>
              {/* Info */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:8 }}>
                  {doc.nom}
                  {(doc.categorie==='appel_fonds' || doc.type==='appel_fonds') && doc.paye && (
                    <span style={{ fontSize:10, fontWeight:700, color:'#047857', background:'#D1FAE5',
                      padding:'2px 8px', borderRadius:99, flexShrink:0 }}>✓ {t('phase_d5.paye_badge')}</span>
                  )}
                </div>
                {doc.fichier_nom && (
                  <div style={{ fontSize:11, color:'#94A3B8', marginTop:2 }}>
                    {doc.fichier_nom}
                    {doc.created_at && ` — ${fmtDate(doc.created_at)}`}
                    {doc.fichier_taille && ` (${Math.round(doc.fichier_taille/1024)} Ko)`}
                  </div>
                )}
              </div>
              {/* Actions */}
              <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                {doc.fichier_chemin && (
                  <>
                    <button onClick={() => ouvrirPreview(doc)} title="Visualiser"
                      style={{ background:'#F8FAFC', border:'0.5px solid #E6E9ED', borderRadius:6,
                        padding:'5px 8px', cursor:'pointer', fontSize:13 }}>🔍</button>
                    <button onClick={() => telecharger(doc)} title="Télécharger"
                      style={{ background:'#F8FAFC', border:'0.5px solid #E6E9ED', borderRadius:6,
                        padding:'5px 8px', cursor:'pointer', fontSize:13 }}>⬇</button>
                  </>
                )}
                {!doc.fichier_chemin && (
                  <button onClick={() => triggerUpload(doc.id)} disabled={uploadingId===doc.id}
                    title="Uploader un fichier"
                    style={{ background:'#F8FAFC', border:'0.5px solid #E6E9ED', borderRadius:6,
                      padding:'5px 8px', cursor:'pointer', fontSize:13 }}>
                    {uploadingId===doc.id ? '…' : '⬆'}
                  </button>
                )}
                {!isVoyageur && !isReadOnly && <button onClick={() => supprimerDoc(doc.id)} title="Supprimer"
                  style={{ background:'#FFF5F5', border:'1px solid #FCA5A5', borderRadius:6,
                    padding:'5px 8px', cursor:'pointer', fontSize:13, color:'#DC2626' }}>🗑</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ajouter un document — masqué voyageur */}
      {!isVoyageur && (!addingDoc ? (
        <button onClick={() => setAddingDoc(true)}
          style={{ fontSize:13, color:'#2563EB', background:'none', border:'1px dashed #93C5FD',
            borderRadius:7, padding:'9px 16px', cursor:'pointer', width:'100%', fontWeight:600 }}>
          {t('sinistres.docs_admin_ajouter')}
        </button>
      ) : (
        <div style={{ background:'#F8FAFC', border:'0.5px solid #E6E9ED', borderRadius:8, padding:14 }}>
          <label style={{ fontSize:12, color:'#64748B', display:'block', marginBottom:5, fontWeight:700 }}>
            {t('sinistres.docs_admin_nom_label')}
          </label>
          <div style={{ display:'flex', gap:8 }}>
            <input value={newDocNom} onChange={e=>setNewDocNom(e.target.value)}
              placeholder="Ex. Contrat prestataire, Courrier assureur…"
              onKeyDown={e=>e.key==='Enter'&&addCustomDoc()}
              autoFocus
              style={{ flex:1, border:'0.5px solid #E6E9ED', borderRadius:6, padding:'7px 10px',
                fontSize:13, fontFamily:'inherit' }}/>
            <button onClick={addCustomDoc} disabled={!newDocNom.trim()}
              style={{ background:'#E8671A', color:'#fff', border:'none', borderRadius:6,
                padding:'7px 14px', cursor:'pointer', fontWeight:700, fontSize:13,
                opacity:!newDocNom.trim()?0.5:1 }}>
              {t('common.add')}
            </button>
            <button onClick={() => { setAddingDoc(false); setNewDocNom(''); }}
              style={{ background:'#F1F5F9', color:'#475569', border:'none', borderRadius:6,
                padding:'7px 10px', cursor:'pointer', fontSize:13 }}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      ))}

      {/* Modal Preview */}
      {previewDoc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1200 }}
          onClick={fermerPreview}>
          <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', maxWidth:'85vw',
            maxHeight:'85vh', display:'flex', flexDirection:'column' }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:'12px 16px', borderBottom:'0.5px solid #E6E9ED',
              display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, fontWeight:700 }}>{previewDoc.fichier_nom || previewDoc.nom}</span>
              <button onClick={fermerPreview}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#64748B' }}>✕</button>
            </div>
            <div style={{ flex:1, overflow:'auto', minHeight:300 }}>
              {previewDoc.fichier_mime?.startsWith('image/') ? (
                <img src={previewUrl} alt={previewDoc.fichier_nom} style={{ maxWidth:'100%' }}/>
              ) : (
                <iframe src={previewUrl} style={{ width:'75vw', height:'75vh', border:'none' }}
                  title={previewDoc.fichier_nom}/>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal : confirmer l'envoi de l'appel de fonds */}
      {showConfirmAF && (
        <div onClick={()=>!confirmingAF && setShowConfirmAF(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(460px, calc(100% - 32px))' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:8, color:theme.text }}>
              {t('phase_solde.af_confirm_title')}
            </div>
            <div style={{ fontSize:13, color:theme.textSecondary, marginBottom:18, lineHeight:1.5 }}>
              {t('phase_solde.af_confirm_desc')}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowConfirmAF(false)} disabled={confirmingAF}
                style={{ padding:'8px 16px', borderRadius:8, border:`0.5px solid ${theme.borderStrong}`, background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmerAppelFonds} disabled={confirmingAF}
                style={{ padding:'8px 18px', borderRadius:8, border:'none',
                  background:theme.orange, color:'#fff', cursor:confirmingAF?'not-allowed':'pointer', fontWeight:500, opacity:confirmingAF?0.6:1 }}>
                {confirmingAF ? t('common.saving') : t('phase_solde.af_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
